import {
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationParticipation, PlatformUser } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import {
  gql,
  QueryHookOptions,
  Reference,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { Client, CLIENT_FIELDS, mapClient } from 'src/graphql/entities/client';
import { CLIENT_EMAIL_FIELDS } from 'src/graphql/entities/client-email';
import { CLIENT_PHONE_FIELDS } from 'src/graphql/entities/client-phone';
import { CONVERSATION_CHANNEL_FIELDS } from 'src/graphql/entities/conversation-channel';
import { PLATFORM_FIELDS } from 'src/graphql/entities/platform-user';
import { ConditionInput, DeepPartial, QueryVariables, Records } from 'src/graphql/types';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { ConversationParticipantRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { removeNotDefinedProperties } from 'src/utils/objects';
import { parseSaveClientErrorMessage } from './parse-save-client-error-message';

export const CONVERSATION_PARTICIPANT_FIELDS = gql`
  fragment ConversationParticipantFields on ConversationParticipant {
    id
    partnerId
    conversationParticipantId
    participantIdentity
    participantEntity
  }
`;

export const CONVERSATION_PARTICIPANT_RELATIONS = gql`
  fragment ConversationParticipantRelations on ConversationParticipant {
    id
    platformUser {
      ...PlatformUserFields
    }
    client {
      ...ClientFields
      primaryEmail {
        ...ClientEmailFields
      }
      emails {
        ...ClientEmailFields
      }
      phoneNumbers {
        ...ClientPhoneFields
      }
    }
    conversationChannels {
      ...ConversationChannelFields
    }
    rsmEmail {
      id
      rsmEmailId
      name
    }
  }
  ${PLATFORM_FIELDS}
  ${CLIENT_FIELDS}
  ${CLIENT_EMAIL_FIELDS}
  ${CLIENT_EMAIL_FIELDS}
  ${CLIENT_PHONE_FIELDS}
  ${CONVERSATION_CHANNEL_FIELDS}
`;

const GET_CONVERSATION_PARTICIPANT_QUERY = gql`
  query GetConversationParticipant($params: QueryParams)
  @entity(name: "ConversationParticipant") {
    getConversationParticipant(queryParams: $params) {
      records {
        ...ConversationParticipantFields
        ...ConversationParticipantRelations
      }
    }
  }
  ${CONVERSATION_PARTICIPANT_FIELDS}
  ${CONVERSATION_PARTICIPANT_RELATIONS}
`;

export const CREATE_PARTICIPANT_MUTATION_QUERY = gql`
  mutation CreateConversationParticipant(
    $partnerId: Int!
    $participantEntity: String!
    $participantIdentity: String!
  ) {
    createConversationParticipant(
      data: {
        partnerId: $partnerId
        participantEntity: $participantEntity
        participantIdentity: $participantIdentity
      }
    ) {
      ...ConversationParticipantFields
    }
  }
  ${CONVERSATION_PARTICIPANT_FIELDS}
`;

export const ASSOCIATE_PARTICIPANT_WITH_CLIENT_MUTATION = gql`
  mutation AssociateParticipantWithClient(
    $partnerId: Int!
    $client: ClientDataInput!
    $participant: ConversationParticipantDataInput
  ) {
    associateParticipantWithClient(
      params: { partnerId: $partnerId, client: $client, participant: $participant }
    ) {
      success
    }
  }
`;

export const UPDATE_PARTICIPANT_MUTATION_QUERY = gql`
  mutation UpdateConversationParticipant(
    $conditions: [ConditionInput]
    $data: ConversationParticipantDataInput
  ) {
    updateConversationParticipant(conditions: $conditions, data: $data) {
      ...ConversationParticipantFields
    }
  }
  ${CONVERSATION_PARTICIPANT_FIELDS}
`;

export interface GetConversationParticipantQuery {
  getConversationParticipant: Records<ConversationParticipantRaw>;
}

export interface CreateConversationParticipantMutation {
  createConversationParticipant: ConversationParticipantRaw[];
}

interface UpdateConversationParticipantMutation {
  updateConversationParticipant: ConversationParticipantRaw[];
}

export type ConversationParticipantItem = ConversationParticipantRaw & {
  client?:
    | (Client & {
        fullName: string;
      })
    | null;
  platformUser?: Pick<PlatformUser, 'id' | 'firstName' | 'lastName' | 'email'> & {
    fullName: string;
  };
};

export const useConversationParticipants = (
  params: {
    participantIds?: string | string[];
    filter?: ConditionInput[];
    limit?: number;
  },
  options?: QueryHookOptions<GetConversationParticipantQuery>,
) => {
  const { filter, participantIds, limit } = params ?? {};
  const queryOptions = useMemo(() => {
    return {
      variables: {
        params: {
          filters: [
            {
              attribute: 'conversationParticipantId',
              operator: 'in',
              value: Array.isArray(participantIds)
                ? [...new Set(participantIds)].sort().join(',')
                : participantIds!,
            },
            ...(filter ?? []),
          ],
          limit: limit ?? MAX_QUERY_LIMIT,
          includeDeleted: true,
        },
      },
      skip: options?.skip || !participantIds?.length,
    };
  }, [filter, limit, options?.skip, participantIds]);

  const { data, previousData, ...rest } = useQuery<
    GetConversationParticipantQuery,
    QueryVariables
  >(GET_CONVERSATION_PARTICIPANT_QUERY, queryOptions);

  const conversationParticipants = useMemo(() => {
    const baseData = data ?? previousData;

    return (baseData?.getConversationParticipant.records.map((participant) => ({
      ...participant,
      platformUser: participant.platformUser && {
        ...participant.platformUser,
        fullName: getFullName(participant.platformUser),
      },
      client: participant.client && mapClient(participant.client),
    })) ?? []) as ConversationParticipantItem[];
  }, [data, previousData]);

  return {
    conversationParticipants,
    ...rest,
  };
};

export const useConversationParticipant = () => {
  const { showErrorAlert } = useNotifications();

  const [createConversationParticipantMutation] =
    useMutation<CreateConversationParticipantMutation>(CREATE_PARTICIPANT_MUTATION_QUERY, {
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong adding a new participant to conversation, please try again',
        );
      },
    });

  const createConversationParticipant = useCallback(
    async ({
      entityId,
      entity,
      partnerId,
    }: {
      entityId: string;
      entity: 'platform_user' | 'client';
      partnerId: number;
    }) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const { data } = await createConversationParticipantMutation({
        variables: {
          data: {
            partnerId,
            participantEntity: entity,
            participantIdentity: entityId,
          },
        },
        optimisticResponse: {
          createConversationParticipant: [
            {
              id: tempId,
              conversationParticipantId: tempId,
              partnerId,
              participantEntity: entity,
              participantIdentity: entityId,
            } as ConversationParticipantRaw,
          ],
        },
      });

      return data?.createConversationParticipant;
    },
    [createConversationParticipantMutation],
  );

  const [updateConversationParticipantMutation] =
    useMutation<UpdateConversationParticipantMutation>(UPDATE_PARTICIPANT_MUTATION_QUERY, {
      onError: (error) => {
        console.error(error);
        showErrorAlert(
          'Something went wrong updating conversation participant, please try again',
        );
      },
    });

  const updateConversationParticipant = useCallback(
    async (conversationParticipant: DeepPartial<ConversationParticipantRaw>) => {
      const patch = removeNotDefinedProperties({
        participantEntity: conversationParticipant.participantEntity,
        participantIdentity: conversationParticipant.participantIdentity,
      });

      const { data } = await updateConversationParticipantMutation({
        variables: {
          conditions: [
            {
              attribute: 'conversationParticipantId',
              operator: '=',
              value:
                conversationParticipant.conversationParticipantId ||
                conversationParticipant.id,
            },
          ],
          data: patch,
        },
        optimisticResponse: {
          updateConversationParticipant: [
            {
              ...conversationParticipant,
              conversationParticipantId:
                conversationParticipant.conversationParticipantId ||
                conversationParticipant.id,
            } as ConversationParticipantRaw,
          ],
        },
        update: (cache, { data: response }) => {
          const created = response?.updateConversationParticipant?.[0];
          if (!created) return;

          const id = cache.identify({
            __typename: 'ConversationParticipant',
            id: created.id || created.conversationParticipantId,
          });

          cache.modify({
            id,
            fields: {
              client(existingItem = {}) {
                const existingClient = existingItem as Reference;

                if (patch.participantEntity === 'client' && patch.participantIdentity) {
                  return conversationParticipant.client ?? existingClient;
                }

                return existingClient;
              },
            },
          });
        },
      });

      return data?.updateConversationParticipant;
    },
    [updateConversationParticipantMutation],
  );

  const [associateParticipantWithClientMutation] = useMutation<{
    associateParticipantWithClient: {
      success: boolean;
    };
  }>(ASSOCIATE_PARTICIPANT_WITH_CLIENT_MUTATION, {
    onError: (error) => {
      console.error(error);

      const message = parseSaveClientErrorMessage(
        error.message,
        'Something went wrong associating client with participant, please try again',
      );
      showErrorAlert(message);
    },
  });

  const associateParticipantWithClient = useCallback(
    async ({
      participant,
      client,
      partnerId,
    }: {
      participant: DeepPartial<ConversationParticipantRaw>;
      client: Partial<Client>;
      partnerId: number;
    }) => {
      const { data } = await associateParticipantWithClientMutation({
        variables: {
          participant: {
            conversationParticipantId: participant.id,
            participantEntity: participant.participantEntity,
            participantIdentity: participant.participantIdentity,
            partnerId: participant.partnerId,
          },
          client: {
            clientId: client.clientId,
            partnerId: client.partnerId,
          },
          partnerId,
        },
        optimisticResponse: {
          associateParticipantWithClient: {
            success: true,
          },
        },
        update: (cache, { data: response }) => {
          if (!response?.associateParticipantWithClient) return;
          if (!participant) return;

          const conversationParticipantId = cache.identify({
            __typename: 'ConversationParticipant',
            id: participant.id,
          });

          const conversationParticipantFields = generateFieldModifiersMap({
            participantEntity: ConversationParticipantEntity.Client,
          });

          cache.modify({
            id: conversationParticipantId,
            fields: {
              ...conversationParticipantFields,
              client(_, { toReference }) {
                return toReference(client, true);
              },
            },
          });
        },
      });

      return data?.associateParticipantWithClient;
    },
    [associateParticipantWithClientMutation],
  );

  return {
    createConversationParticipant,
    updateConversationParticipant,
    associateParticipantWithClient,
  };
};

export const useGroupedConversationParticipants = (
  participations?: ConversationParticipation[],
) => {
  const participantModes = useMemo(
    () =>
      new Map(
        participations?.map((participation) => [
          participation.channel.conversationParticipantId,
          participation.participationMode,
        ]) ?? [],
      ),
    [participations],
  );

  const participantIds = [...participantModes.keys()];

  const { conversationParticipants: participants, loading } = useConversationParticipants({
    participantIds,
  });

  const groupedParticipants = useMemo(
    () =>
      participants.reduce<
        Record<
          ConversationParticipationMode.Assist | ConversationParticipationMode.Engage,
          ConversationParticipantItem[]
        >
      >(
        (acc, participant) => {
          const mode = participantModes.get(
            participant.id,
          ) as ConversationParticipationMode.Engage;

          if (mode in acc) {
            acc[mode].push(participant);
          }

          return acc;
        },
        {
          [ConversationParticipationMode.Assist]: [],
          [ConversationParticipationMode.Engage]: [],
        },
      ),
    [participants, participantModes],
  );

  return { groupedParticipants, participants, loading };
};
